/**
 * The console shell both portals share: sidebar (248 px, a 68 px rail when
 * folded; the toggle lives in the top bar, also on "["), a 56 px top bar with
 * breadcrumbs, the ⌘K palette, the theme switch and the account menu, and an
 * optional 40 px inverse context band above everything.
 */
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { Icon } from "./Icon";
import { Avatar, Button } from "./ui";
import { Menu, Tip } from "./overlays";
import { TitleCtx, composeTitle, type TitleRegistry } from "./title";

export type NavItem = { to: string; label: string; icon: string; count?: number | string; badge?: boolean; locked?: string | false; end?: boolean };
export type NavSection = { title?: string; items: NavItem[] };
export type SearchResult = { kind: string; title: string; sub?: string; href: string; icon?: string };

// ---------------------------------------------------------------- theme

export type Theme = "light" | "dark" | "system";
function apply(t: Theme) {
  const dark = t === "dark" || (t === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}
export function useTheme(): [Theme, (t: Theme) => void] {
  const [t, setT] = useState<Theme>(() => {
    try {
      return (localStorage.getItem("inlinea.theme") as Theme) || "light";
    } catch {
      return "light";
    }
  });
  useEffect(() => {
    apply(t);
    try {
      localStorage.setItem("inlinea.theme", t);
    } catch {
      /* private mode */
    }
    if (t !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const fn = () => apply("system");
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [t]);
  return [t, setT];
}

// ---------------------------------------------------------------- palette

const uniq = (rs: SearchResult[]) => {
  const seen = new Set<string>();
  return rs.filter((r) => !seen.has(r.href) && !!seen.add(r.href));
};

export function Palette({ open, onClose, search, quick, placeholder = "Search tenants, partners, tickets, pages" }: { open: boolean; onClose: () => void; search: (q: string) => Promise<SearchResult[]>; quick: SearchResult[]; placeholder?: string }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState<SearchResult[]>([]);
  const [hl, setHl] = useState(0);
  const nav = useNavigate();
  const seq = useRef(0);
  useEffect(() => {
    if (open) {
      setQ("");
      setHl(0);
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const n = ++seq.current;
    const local = uniq(quick.filter((r) => !q || `${r.title} ${r.sub ?? ""}`.toLowerCase().includes(q.toLowerCase())));
    setRes(local);
    if (q.trim().length < 2) return;
    // One row per place: a server result wins over the same page or record from the quick list.
    const t = setTimeout(() => search(q).then((r) => n === seq.current && setRes(uniq([...r, ...local]))).catch(() => undefined), 140);
    return () => clearTimeout(t);
  }, [q, open, quick, search]);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    list.current?.children[hl]?.scrollIntoView({ block: "nearest" });
  }, [hl, res]);
  if (!open) return null;
  const go = (r: SearchResult) => {
    onClose();
    nav(r.href);
  };
  return (
    <>
      <div className="backdrop" style={{ zIndex: 80 }} onMouseDown={onClose} />
      <div
        className="palette"
        role="dialog"
        aria-label="Search"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") (e.preventDefault(), setHl((h) => Math.min(res.length - 1, h + 1)));
          if (e.key === "ArrowUp") (e.preventDefault(), setHl((h) => Math.max(0, h - 1)));
          if (e.key === "Enter" && res[hl]) go(res[hl]);
          if (e.key === "Escape") (e.stopPropagation(), onClose());
        }}
      >
        <div style={{ position: "relative" }}>
          <Icon name="search" size={18} style={{ position: "absolute", left: 16, top: 17, color: "var(--tx3)" }} />
          <input autoFocus value={q} onChange={(e) => (setQ(e.target.value), setHl(0))} placeholder={placeholder} aria-label="Search" />
        </div>
        <div className="res" ref={list}>
          {res.map((r, i) => (
            <button key={r.href} className={i === hl ? "hl" : ""} onMouseEnter={() => setHl(i)} onClick={() => go(r)}>
              <span className="tile sm">
                <Icon name={r.icon ?? "search"} size={15} />
              </span>
              <span className="grow" style={{ minWidth: 0 }}>
                <div className="ellipsis" style={{ fontWeight: 500 }}>{r.title}</div>
                {r.sub && <div className="faint ellipsis" style={{ fontSize: 12 }}>{r.sub}</div>}
              </span>
              <span className="chip">{r.kind}</span>
            </button>
          ))}
          {!res.length && <div className="faint" style={{ padding: 14 }}>No matches. Try a tenant name, a domain or a ticket number.</div>}
        </div>
        <div className="foot">
          <span><span className="kbd">↑↓</span> Navigate</span>
          <span><span className="kbd">↵</span> Open</span>
          <span><span className="kbd">Esc</span> Close</span>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- shell

export type ShellProps = {
  /** `title` names the product at the end of every tab title; it defaults to "<name> <sub>". */
  product: { name: string; sub: string; tag?: string; logo?: string; title?: string };
  nav: NavSection[];
  header?: ReactNode; // above the nav, under search (context switcher)
  crumbs: ReactNode;
  topRight?: ReactNode;
  user: { name: string; email: string; lines?: ReactNode; menu: { label: string; icon: string; onClick: () => void; hint?: string }[] };
  version: ReactNode;
  band?: ReactNode;
  bandTone?: "amber";
  search: (q: string) => Promise<SearchResult[]>;
  quick: SearchResult[];
  children: ReactNode;
  searchLabel?: string;
  /** What the search box says it finds; each console names what its search covers. */
  searchPlaceholder?: string;
};

const typing = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

export function Shell(p: ShellProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("inlinea.sidebar") === "collapsed";
    } catch {
      return false;
    }
  });
  const [mobile, setMobile] = useState(false);
  const [palette, setPalette] = useState(false);
  const [theme, setTheme] = useTheme();
  const loc = useLocation();
  const navEl = useRef<HTMLElement>(null);
  const [ind, setInd] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => setMobile(false), [loc.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem("inlinea.sidebar", collapsed ? "collapsed" : "open");
    } catch {
      /* private mode */
    }
  }, [collapsed]);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((x) => !x);
      } else if (e.key === "[" && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e.target) && !window.matchMedia("(max-width: 900px)").matches) {
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);
  // The highlight slides to the active item instead of jumping between rows.
  useLayoutEffect(() => {
    const a = navEl.current?.querySelector<HTMLElement>(".navi.active");
    setInd(a ? { top: a.offsetTop, height: a.offsetHeight } : null);
  }, [loc.pathname, p.nav, collapsed]);
  // The one toggle: it folds the rail on a wide screen and opens the drawer on a narrow one.
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 900px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const fn = () => setNarrow(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  const toggle = () => (narrow ? setMobile((m) => !m) : setCollapsed((c) => !c));
  // One button, two states. A stored "system" choice shows as whatever the system is now.
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const quick = useMemo(() => p.quick, [p.quick]);
  const rail = collapsed && !narrow;
  // The tab title: breadcrumbs register their labels, page headings register a name (title.ts).
  const [crumbParts, setCrumbParts] = useState<string[]>([]);
  const [headings, setHeadings] = useState<{ id: number; title: string }[]>([]);
  const registry = useMemo<TitleRegistry>(() => {
    let n = 0;
    return {
      crumbs: (parts) => setCrumbParts((x) => (x.join("\u0000") === parts.join("\u0000") ? x : parts)),
      heading: (title) => {
        const id = ++n;
        setHeadings((h) => [...h, { id, title }]);
        return () => setHeadings((h) => h.filter((x) => x.id !== id));
      },
    };
  }, []);
  const productTitle = p.product.title ?? `${p.product.name} ${p.product.sub}`.trim();
  const docTitle = composeTitle(crumbParts, headings[headings.length - 1]?.title, productTitle, p.product.sub);
  useEffect(() => {
    document.title = docTitle;
  }, [docTitle]);
  const searchName = `${p.searchLabel ?? "Search"} (⌘K)`;
  return (
    <TitleCtx.Provider value={registry}>
    <div className={`app ${p.band ? "has-band" : ""} ${collapsed ? "collapsed-shell" : ""}`}>
      {p.band && <div className={`band ${p.bandTone ?? ""}`}>{p.band}</div>}
      <div className={`scrim${mobile ? " open" : ""}`} onClick={() => setMobile(false)} />
      <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobile ? "open" : ""}`} aria-label="Main">
        <NavLink to="/" className="logo" aria-label={`${p.product.name} ${p.product.sub}`}>
          <img src={p.product.logo ?? "/brand/inlinea-mark.svg"} alt="" />
          <span className="grow" style={{ minWidth: 0 }}>
            <b className="ellipsis">{p.product.name}</b>
            <small>
              {p.product.sub}
              {p.product.tag && <span className="tag">{p.product.tag}</span>}
            </small>
          </span>
        </NavLink>
        <Tip text={rail ? searchName : ""} side="right" block>
          <button className="searchbox" onClick={() => setPalette(true)} aria-label={searchName} aria-haspopup="dialog" aria-expanded={palette}>
            <span className="ni">
              <Icon name="search" size={15} />
            </span>
            <span className="navl grow ellipsis" style={{ textAlign: "left" }}>{p.searchLabel ?? "Search"}</span>
            <span className="kbd">⌘K</span>
          </button>
        </Tip>
        {p.header && !rail && <div className="sidehead">{p.header}</div>}
        <nav ref={navEl}>
          <span className="navind" style={ind ? { transform: `translateY(${ind.top}px)`, height: ind.height } : { opacity: 0 }} />
          {p.nav.map((s, i) => (
            <div key={i}>
              {s.title && <div className="navsec">{s.title}</div>}
              {s.items.map((it) => (
                <Tip key={it.to} text={rail ? it.label : it.locked || ""} side="right" block>
                  <NavLink to={it.to} end={it.end} className={({ isActive }) => `navi${isActive ? " active" : ""}${it.locked ? " locked" : ""}`} aria-label={it.label}>
                    <span className="ni">
                      <Icon name={it.locked ? "lock-closed" : it.icon} size={17} />
                    </span>
                    <span className="navl grow ellipsis">{it.label}</span>
                    {it.count !== undefined && it.count !== 0 && <span className={`count${it.badge ? " badge" : ""}`}>{it.count}</span>}
                  </NavLink>
                </Tip>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidefoot">
          <span className="ni">
            <img src="/brand/inlinea-mark.svg" alt="" width={18} height={18} />
          </span>
          <span className="fade">{p.version}</span>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <Tip text={narrow ? "" : collapsed ? "Expand the sidebar ( [ )" : "Collapse the sidebar ( [ )"}>
            <Button kind="ghost" iconOnly icon={narrow ? "navigation" : collapsed ? "panel-left-expand" : "panel-left-contract"} aria-label={narrow ? "Open the menu" : collapsed ? "Expand the sidebar" : "Collapse the sidebar"} aria-expanded={narrow ? mobile : !collapsed} onClick={toggle} />
          </Tip>
          <div className="crumbs grow">{p.crumbs}</div>
          {p.topRight}
          <Tip text={isDark ? "Switch to light" : "Switch to dark"}>
            <Button iconOnly icon={isDark ? "weather-moon" : "weather-sunny"} aria-label={isDark ? "Switch to light" : "Switch to dark"} onClick={() => setTheme(isDark ? "light" : "dark")} />
          </Tip>
          <Menu
            header={
              <>
                <Avatar name={p.user.name} round color="var(--blF)" />
                <span className="grow" style={{ minWidth: 0 }}>
                  <div className="ellipsis" style={{ fontWeight: 600 }}>{p.user.name}</div>
                  <div className="faint ellipsis" style={{ fontSize: 12 }}>{p.user.email}</div>
                  {p.user.lines}
                </span>
              </>
            }
            items={p.user.menu.map((m) => ({ label: m.label, icon: m.icon, onClick: m.onClick, hint: m.hint }))}
            trigger={(open, isOpen) => (
              <button type="button" className="acct" onClick={open} aria-label="Account" aria-haspopup="menu" aria-expanded={isOpen}>
                <Avatar name={p.user.name} size="sm" round color="var(--blF)" />
                <Icon name="chevron-down" size={13} style={{ color: "var(--tx3)" }} />
              </button>
            )}
          />
        </header>
        {p.children}
      </div>
      <Palette open={palette} onClose={() => setPalette(false)} search={p.search} quick={quick} placeholder={p.searchPlaceholder} />
    </div>
    </TitleCtx.Provider>
  );
}

export function Crumbs({ items }: { items: (string | { label: string; to: string })[] }) {
  const reg = useContext(TitleCtx);
  const labels = items.map((it) => (typeof it === "string" ? it : it.label));
  const key = labels.join("\u0000");
  useEffect(() => reg?.crumbs(labels), [reg, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      {items.map((it, i) => (
        <span key={i} className="row" style={{ gap: 8, minWidth: 0 }}>
          {i > 0 && <Icon name="chevron-right" size={12} className="sep" />}
          {typeof it === "string" ? (
            i === items.length - 1 ? <b className="ellipsis" aria-current="page">{it}</b> : <span className="ellipsis">{it}</span>
          ) : (
            <Link to={it.to} className="ellipsis">{it.label}</Link>
          )}
        </span>
      ))}
    </>
  );
}
